import db from "../../database";
import { trace } from "../../utils/debugTrace";

// Diagnostics must never break printing (localStorage can throw / be full): every trace in the print path goes through this.
export const safeTrace = (message, data) => {
  try {
    trace("GiftCardPrint", message, data);
  } catch {
    // ignore
  }
};

// Every print call site only knows a digital card as giftCardType "DIG" and passes type
// "Gift Card" (or "Credit Note" on a return) -- a Voucher or a Credit Note issued from the Gift
// Card drawer is a Gift Card whose gift card type belongs to the "Voucher" / "Credit Note" group,
// and that group is dropped from the cart line. Resolve it here from the synced db.giftCardData
// record of the printed product so it picks up the till's own template without touching the cart
// line, order payload or call sites.
const groupToPrintType = (group) => {
  const value = typeof group === "string" ? group.trim().toLowerCase() : "";
  if (value === "voucher" || value === "gv") return "Voucher";
  if (value === "credit note" || value === "cn") return "Credit Note";
  return null;
};

export const resolveGiftCardPrintType = async (printData, type) => {
  if (type !== "Gift Card") {
    safeTrace("resolveType: kept as requested", { requested: type, reason: "only 'Gift Card' requests are re-routed" });
    return type;
  }
  const productId = printData?.mProductId || printData?.productId;
  if (!productId) {
    safeTrace("resolveType: kept Gift Card", { reason: "print line has no mProductId/productId", lineKeys: Object.keys(printData || {}).slice(0, 40) });
    return type;
  }
  try {
    const records = (await db.giftCardData?.toArray()) ?? [];
    // A product can be linked to more than one gift card type (seen on OFC-QA: one product in both the
    // Gift Card and Voucher groups). The printed line can't say which one was sold, so only re-route when
    // every matching type is in the same Voucher / Credit Note group; otherwise keep the Gift Card route.
    const matches = records.filter((record) => record?.mProductId === productId);
    const groups = new Set(matches.map((record) => groupToPrintType(record?.giftCardGroup)));
    const resolved = groups.size === 1 && !groups.has(null) ? [...groups][0] : type;
    safeTrace(`resolveType: ${type} -> ${resolved}`, {
      productId,
      giftCardDataCount: records.length,
      matches: matches.map((record) => ({ typeId: record?.cwrGiftcardTypeId, name: record?.name, group: record?.giftCardGroup, type: record?.type })),
      reason:
        matches.length === 0
          ? "no synced gift card type for this product (db.giftCardData)"
          : resolved === type
            ? "matching type(s) not all in one Voucher/Credit Note group"
            : "all matching types in one group",
    });
    return resolved;
  } catch (error) {
    console.error("[GiftCardPrint] could not resolve print type, keeping", type, error);
    safeTrace("resolveType: error, kept Gift Card", { error: String(error?.message || error) });
    return type;
  }
};

// Till-level template for a print type: the type's own template, else the till's Gift Card template,
// so a till that has any of its own templates never falls through to the shared legacy one.
export const getTillTemplateXml = (cwrTill, type) => {
  const own = {
    "Gift Card": "giftCardPrintTemplate",
    "Credit Note": "creditNotePrintTemplate",
    Voucher: "voucherPrintTemplate",
  }[type];
  const xml = own ? cwrTill?.[own]?.xmlcode2 : null;
  return xml || cwrTill?.giftCardPrintTemplate?.xmlcode2 || null;
};

// ---- Diagnostics: persistent ring buffer, read with __dumpTrace() in DevTools. Never throws, never logs template bodies.
const describeTemplate = (template) => (template ? { id: template.cwrPrinttemplateId || null, name: template.name || null, length: template.xmlcode2 ? template.xmlcode2.length : 0 } : null);

export const describeTillTemplates = (cwrTill) => ({
  till: cwrTill?.till || cwrTill?.searchKey || null,
  cwrTillID: cwrTill?.cwrTillID || null,
  controller: cwrTill?.hardwareController?.imageUrl || null,
  giftCardPrintTemplate: describeTemplate(cwrTill?.giftCardPrintTemplate),
  creditNotePrintTemplate: describeTemplate(cwrTill?.creditNotePrintTemplate),
  voucherPrintTemplate: describeTemplate(cwrTill?.voucherPrintTemplate),
});

export const describePrintLine = (line) => ({
  name: line?.name || null,
  cardNo: line?.cardNo || null,
  nettotal: line?.nettotal ?? null,
  giftCardType: line?.giftCardType || null,
  mProductId: line?.mProductId || null,
  productId: line?.productId || null,
});

export const templateFormat = (text) => {
  const value = typeof text === "string" ? text.trim() : "";
  if (!value) return "EMPTY";
  return value.startsWith("<") ? "XML" : value.startsWith("{") ? "JSON" : "UNKNOWN";
};

// Traces what a receipt print reached the gift card gate with, so "nothing printed" can be told apart from
// "printed the wrong template": a gift card line that is not "DIG" (or has no giftCardType) never reaches a printer.
export const traceGiftCardLines = (site, receipt) => {
  try {
    const items = Array.isArray(receipt?.items) ? receipt.items : [];
    const giftLines = items.filter((item) => item?.giftCardType || item?.isGiftCard || item?.cardNo);
    if (giftLines.length === 0) return;
    safeTrace("receipt reached the gift card gate", {
      site,
      order: receipt?.documentno || receipt?.documentNo || receipt?.sOrderID || null,
      isReturn: !!receipt?.isReturn,
      itemCount: items.length,
      willPrint: giftLines.filter((item) => item?.giftCardType === "DIG").length,
      lines: giftLines.map((item) => ({ ...describePrintLine(item), isGiftCard: !!item?.isGiftCard })),
    });
  } catch {
    // diagnostics must never break printing
  }
};

// The receipt copy itself failed to print, so the gift card / voucher / credit note slips after it are skipped.
export const traceGiftCardSkipped = (site) => {
  try {
    safeTrace("receipt copy failed, gift card slips skipped", { site });
  } catch {
    // diagnostics must never break printing
  }
};

// Gift card / voucher / credit note lines on a receipt printed in a mode that has no gift card slip at all (html / xml print).
export const traceGiftCardUnsupported = (mode, receipt) => {
  try {
    const items = Array.isArray(receipt?.items) ? receipt.items : [];
    const giftLines = items.filter((item) => item?.giftCardType || item?.isGiftCard || item?.cardNo);
    if (giftLines.length === 0) return;
    safeTrace(`receipt has gift card lines but ${mode} print mode prints no gift card slip`, {
      mode,
      order: receipt?.documentno || receipt?.documentNo || receipt?.sOrderID || null,
      lines: giftLines.map(describePrintLine),
    });
  } catch {
    // diagnostics must never break printing
  }
};

// What the gift card type sync brought into db.giftCardData, per group and type (Gift Card / Voucher / Credit Note x DIG / PHY).
export const summarizeGiftCardTypes = (types) => {
  const summary = {};
  (Array.isArray(types) ? types : []).forEach((record) => {
    const key = `${record?.giftCardGroup || "?"}/${record?.type || "?"}`;
    const entry = summary[key] || (summary[key] = { count: 0, withoutTemplate: 0 });
    entry.count += 1;
    if (!record?.printTemplate?.xmlcode2) entry.withoutTemplate += 1;
  });
  return summary;
};
